import React from 'react';
import ArrayUtil from '@leverege/array-util';
import { GlobalState } from '@leverege/ui-redux';
import { Toast } from '@leverege/ui-elements';
import { EditorDialog } from '@leverege/ui-plugin';
import { UserValidator } from '@leverege/api-validator';
import I18N from '@leverege/i18n';
import Rsrc from "../Rsrc.js";
import Icons from "../Icons.js";
import AddEditUserEditor from "./AddEditUserEditor.js";
/**
 * Add user dialog.
 */
const {
  t
} = I18N.ns('userManagement.user.AddEditUserDialog');
export default class AddEditUserDialog extends React.Component {
  /**
   * Constructs component.
   */
  constructor(props) {
    super(props);
    this.state = {
      title: Rsrc.addUserTitle,
      okayLabel: Rsrc.addUserOkay,
      error: null
    };
  }

  /**
   * Runs once when component mounts to initialize edit-user information.
   */
  componentDidMount() {
    const {
      edit
    } = this.props;
    if (edit) {
      this.setState({
        title: Rsrc.editUserTitle,
        okayLabel: t('okayLabel')
      });
    }
  }

  /**
   * Handles dialog completion events.
   */
  onOkay = async event => {
    switch (event?.value?.selectionType) {
      case 'invite':
      case 'create':
      case 'oidcAccess':
        return this.onAddUser(event);
      case 'update':
      case 'apiAccess':
        return this.onUpdateUser(event);
    }
    return console.log('Invalid selection type', event?.value?.selectionType);
  };

  /**
   * Handles adding users.
   */
  async onAddUser(event) {
    // Get parameters
    const {
      actions,
      onClose,
      uiName,
      emailOptions,
      smsOptions,
      allowSmsInvitation
    } = event.data;
    const {
      user: rawUser,
      roles,
      selectionType
    } = event.value;
    const state = GlobalState.get();
    const user = {
      ...rawUser
    };
    let resolvedEmailUiName = emailOptions?.uiName || uiName;
    let resolvedSmsUiName = smsOptions?.uiName || uiName;
    if (typeof uiName === 'function') {
      resolvedEmailUiName = uiName({
        user: {
          ...rawUser,
          roles
        },
        methods: ['email']
      });
      resolvedSmsUiName = uiName({
        user: {
          ...rawUser,
          roles
        },
        methods: ['sms']
      });
    }

    // Clean up password confirmation
    if (user?.confirmPassword !== undefined) {
      delete user.confirmPassword;
    }

    // Ensure email is lowercase
    if (typeof user?.email === 'string') {
      user.email = user.email.toLowerCase();
    }

    // Ensure username is lowercase
    if (typeof user?.username === 'string') {
      user.username = user.username.toLowerCase();
    }

    // Initialize new user data
    const userData = {
      user,
      roles: roles.map(r => r.id),
      sendEmail: selectionType === 'invite',
      emailOptions: {
        ...emailOptions,
        uiName: resolvedEmailUiName,
        inviter: {
          name: state?.auth?.profile?.name,
          email: state?.auth?.profile?.email,
          ...emailOptions?.inviter
        }
      }
    };
    if (selectionType === 'invite' && allowSmsInvitation) {
      userData.sendSms = true;
      userData.smsOptions = {
        ...smsOptions,
        uiName: resolvedSmsUiName,
        inviter: {
          name: state?.auth?.profile?.name,
          email: state?.auth?.profile?.email,
          ...smsOptions?.inviter
        }
      };
    }

    // Try to create user
    try {
      await GlobalState.dispatch(actions.users().add(userData));
    } catch (error) {
      const toast = selectionType === 'invite' ? t('inviteUserFailed') : selectionType === 'create' ? t('createUserFailed') : t('addUserFailed');
      Toast.error([toast, error.message]);
      return onClose();
    }

    // Successfully created user
    const toast = selectionType === 'invite' ? t('invitedUser') : selectionType === 'create' ? t('createdUser') : t('addedUser');
    Toast.success([toast]);
    onClose();
  }
  async onUpdateUser(event) {
    // Get parameters
    const {
      actions,
      onClose
    } = event.data;
    const {
      user,
      roles,
      originalRoles
    } = event.value;
    const rolesDiff = ArrayUtil.diffByKey(originalRoles, roles, 'id');

    // Validate parameters
    if (user?.id == null) {
      return console.log('User id must be defined');
    }

    // Update roles
    try {
      if (rolesDiff.added.length > 0) {
        const msg = {
          sendEmail: true,
          userId: user.id,
          roles: rolesDiff.added.map(r => r.id)
        };
        await GlobalState.dispatch(actions.users().add(msg));
        // await GlobalState.dispatch( actions.user( user.id ).addRoles( rolesDiff.added.map( r => r.id ) ) )
      }
      if (rolesDiff.removed.length > 0) {
        await GlobalState.dispatch(actions.user(user.id).removeRoles(rolesDiff.removed.map(r => r.id)));
      }
    } catch (error) {
      Toast.error([t('toastError'), error.message]);
      return onClose();
    }

    // Successfully updated user
    Toast.success([t('toastSuccess')]);
    onClose();
  }

  /**
   * Validator to enable submission button.
   */
  validator(data) {
    let error = t('defaultError');
    switch (data?.selectionType) {
      case 'invite':
        error = UserValidator.invite(data);
        break;
      case 'create':
        error = UserValidator.create(data);
        break;
      case 'update':
      case 'apiAccess':
        error = UserValidator.roles(data?.roles);
        break;
      case 'oidcAccess':
        error = UserValidator.oidcAccess(data);
        break;
      default:
        break;
    }
    return error == null;
  }

  /**
   * Renders component.
   */
  render() {
    // Get parameters
    const {
      show,
      onClose,
      projectId,
      selectorProps,
      systemId,
      actions,
      Selector,
      allowed,
      edit,
      user,
      editorVariants,
      allowApiAccess,
      api
    } = this.props;
    const {
      title,
      okayLabel
    } = this.state;

    // Render component
    return /*#__PURE__*/React.createElement(EditorDialog, {
      icon: Icons.addDialogIcon,
      title: title,
      okayLabel: okayLabel,
      cancelLabel: t('cancelLabel'),
      eventData: {
        ...this.props,
        emailOptions: {
          systemId
        }
      },
      show: show,
      onClose: onClose,
      onOkay: this.onOkay,
      validator: this.validator,
      editor: AddEditUserEditor,
      editorVariants: {
        pane: 'userEditor|rootEditor|form',
        header: 'userEditorHeader|rootEditorHeader|formHeader',
        body: 'userEditorContent|rootEditorContent|formBody',
        title: 'userEditorTitle|rootEditorTitle|formTitle',
        footer: 'userEditorFooter|rootEditorFooter|formFooter',
        ...editorVariants
      },
      editorProps: {
        canCreateUsers: allowed?.createUser || false,
        selectorProps,
        projectId,
        actions,
        api,
        edit,
        user,
        roleSelector: Selector,
        allowApiAccess,
        setDialogTitle: title => {
          this.setState({
            title
          });
        },
        setDialogOkayLabel: okayLabel => {
          this.setState({
            okayLabel
          });
        }
      }
    });
  }
}